import { keys, map, values } from 'lodash-es'
import { API } from '#app/lib/api/api'
import { assertString } from '#app/lib/assert_types'
import { getColorHexCodeFromCouchUuId, getColorSquareDataUri } from '#app/lib/images/images'
import preq, { treq } from '#app/lib/preq'
import { getVisibilitySummary, getVisibilitySummaryLabel, visibilitySummariesData, type VisibilitySummaryData, type VisibilitySummaryLabel } from '#general/lib/visibility'
import type { ShelvesByIdsResponse } from '#server/controllers/shelves/by_ids'
import type { ShelvesByOwnersResponse } from '#server/controllers/shelves/by_owners'
import type { ColorHexCode, RelativeUrl } from '#server/types/common'
import type { ImageDataUrl } from '#server/types/image'
import type { ItemId } from '#server/types/item'
import type { Shelf, ShelfId } from '#server/types/shelf'
import type { UserId } from '#server/types/user'
import { mainUser } from '#user/lib/main_user'

export function getShelfById (id: ShelfId) {
  return preq.get(API.shelves.byIds(id))
  .then(getShelf)
}

export async function getShelvesByIds (ids: ShelfId[]) {
  if (ids.length === 0) return []
  const { shelves } = await treq.get<ShelvesByIdsResponse>(API.shelves.byIds(ids))
  return values(shelves).sort(byName).map(serializeShelf)
}

export async function createShelf (params: Partial<Shelf> & { items?: ItemId[] }) {
  const { shelf } = await preq.post(API.shelves.create, params)
  return serializeShelf(shelf)
}

export async function updateShelf (params: { shelf: ShelfId & Partial<Shelf> }) {
  try {
    const { shelf } = await preq.post(API.shelves.update, params)
    return shelf
  } catch (err) {
    if (err.message !== 'nothing to update') {
      throw err
    }
  }
}

export function deleteShelf (params) {
  return preq.post(API.shelves.delete, params)
}

export function removeItemsFromShelf ({ shelfId, items }) {
  const itemsIds = map(items, '_id')
  return shelfActionReq(shelfId, itemsIds, 'removeItems')
}

export function addItemsToShelf ({ shelfId, items }) {
  const itemsIds = map(items, '_id')
  return shelfActionReq(shelfId, itemsIds, 'addItems')
}

export async function addItemsByIdsToShelf ({ shelfId, itemsIds }) {
  return shelfActionReq(shelfId, itemsIds, 'addItems')
}

export async function getShelvesByOwner (userId: UserId) {
  assertString(userId)
  const { shelves } = await treq.get<ShelvesByOwnersResponse>(API.shelves.byOwners(userId))
  return values(shelves).sort(byName).map(serializeShelf)
}

export async function countShelves (userId: UserId) {
  const { shelves } = await treq.get<ShelvesByOwnersResponse>(API.shelves.byOwners(userId))
  return keys(shelves).length
}

function shelfActionReq (id: ShelfId, itemsIds: ItemId[], action: 'addItems' | 'removeItems') {
  return preq.post(API.shelves[action], { id, items: itemsIds })
  .then(getShelf)
}

const getShelf = ({ shelves }: { shelves: Record<ShelfId, Shelf> }) => values(shelves)[0]

export interface SerializedShelf extends Shelf {
  pathname: RelativeUrl
  picture: ImageDataUrl
  isEditable: boolean
  iconData?: VisibilitySummaryData
  iconLabel?: VisibilitySummaryLabel
}

export function serializeShelf (shelf: Shelf) {
  const { _id, visibility } = shelf
  let { color } = shelf
  color ??= `#${getColorHexCodeFromCouchUuId(_id)}` as ColorHexCode
  const pathname = `/shelves/${_id}`
  const picture = getColorSquareDataUri(color)
  const isEditable = shelf.owner === mainUser?._id
  let iconData, iconLabel
  if (visibility) {
    const visibilitySummary = getVisibilitySummary(visibility)
    iconData = visibilitySummariesData[visibilitySummary]
    iconLabel = getVisibilitySummaryLabel(visibility)
  }
  return {
    ...shelf,
    color,
    pathname,
    picture,
    isEditable,
    iconData,
    iconLabel,
  } as SerializedShelf
}

export function getShelfMetadata (shelf: Shelf) {
  const { _id: shelfId, name, description, picture, pathname } = serializeShelf(shelf)
  return {
    title: name,
    description,
    image: picture,
    url: pathname,
    rss: API.feeds('shelf', shelfId),
  }
}

const byName = (a, b) => normalizeName(a.name) > normalizeName(b.name) ? 1 : -1

const normalizeName = name => name.toLowerCase()
